import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Button,
  TextField,
  MenuItem,
} from '@mui/material';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useNavigate } from 'react-router-dom';
import { updateSubscription } from './SubscriptionService'; // Ensure correct path
import { notify } from 'utils/notify';
import {
  getScheduleItemsByLoggedInUser,
  createSchedule,
} from './ScheduleService';

const ScheduleSection = ({ appCode, subscriptionId }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [scheduleExists, setScheduleExists] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [intervalUnit, setIntervalUnit] = useState('minutes');
  const [intervalDuration, setIntervalDuration] = useState('1');
  const navigate = useNavigate();

  // Options for interval units
  const intervalOptions = {
    minutes: ['1', '5', '10', '30'],
    hours: ['1', '2'],
    days: ['1', '2', '3'],
    weeks: ['1', '2', '3'],
    months: ['1', '2', '3'],
    years: ['1'],
  };

  useEffect(() => {
    if (appCode) {
      listScheduleItems(appCode);
    }
  }, [appCode]);

  const listScheduleItems = async (appCode) => {
    setLoading(true);
    try {
      const response = await getScheduleItemsByLoggedInUser(appCode);
      if (
        response.data &&
        Array.isArray(response.data) &&
        response.data.length > 0
      ) {
        setItems(response.data);
        setScheduleExists(true);
      } else {
        setItems([]);
        setScheduleExists(false);
      }
    } catch (error) {
      console.error('Error fetching schedule items:', error);
      setScheduleExists(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSchedule = async () => {
    if (!selectedDate) {
      notify('Please select a valid date.');
      return;
    }

    const scheduleData = {
      startDate: selectedDate.toISOString(),
      increment: intervalDuration,
      unit: intervalUnit,
      id: 1,
      appCode,
    };

    try {
      await createSchedule(scheduleData);
      listScheduleItems(appCode); // Refresh schedule after creation
    } catch (error) {
      console.error('Error creating schedule:', error);
    }
  };

  const handleGetStarted = async () => {
    try {
      //const subscriptionId = 29; // Replace with dynamic subscription ID if needed
      const updatedSubscription = {
        onboardingStatus: 'Complete',
      };

      await updateSubscription(subscriptionId, updatedSubscription);
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Error updating subscription:', error);
      notify('Failed to update subscription. Please try again.');
    }
  };

  return (
    <Box>
      {loading && <Typography>Loading...</Typography>}
      <Typography variant="h4" marginBottom={4}>
        Schedule
      </Typography>

      {!scheduleExists ? (
        <Box marginBottom={4}>
          <Typography variant="h5">Create Schedule</Typography>

          <Box marginBottom={4}>
            <Typography variant="h6">Set schedule start date</Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                value={selectedDate}
                onChange={(newDate) => setSelectedDate(newDate)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Box>

          <Box marginBottom={4}>
            <Typography variant="h6">
              Select Interval between assignments
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={6} sm={4}>
                <TextField
                  label="Interval Duration"
                  variant="outlined"
                  fullWidth
                  select
                  value={intervalDuration}
                  onChange={(e) => setIntervalDuration(e.target.value)}
                >
                  {intervalOptions[intervalUnit].map((duration) => (
                    <MenuItem key={duration} value={duration}>
                      {duration}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={6} sm={4}>
                <TextField
                  label="Interval Unit"
                  variant="outlined"
                  fullWidth
                  select
                  value={intervalUnit}
                  onChange={(e) => {
                    setIntervalUnit(e.target.value);
                    setIntervalDuration(intervalOptions[e.target.value][0]);
                  }}
                >
                  {Object.keys(intervalOptions).map((unit) => (
                    <MenuItem key={unit} value={unit}>
                      {unit.charAt(0).toUpperCase() + unit.slice(1)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={8} sm={4}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateSchedule}
                >
                  Create Schedule
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ) : (
        <Box>
          <Typography variant="h5" marginBottom={4}>
            View Schedule
          </Typography>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 750 }} aria-label="Selected Items Table">
              <TableHead sx={{ bgcolor: 'alternate.main' }}>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Schedule Item</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.id}</TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.startDate}</TableCell>
                    <TableCell>{item.endDate}</TableCell>
                    <TableCell>{item.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        marginTop={4}
      >
        <Button
          variant="contained"
          color="success"
          style={{ padding: '16px 32px', fontSize: '18px' }}
          onClick={handleGetStarted}
          disabled={!scheduleExists}
        >
          Get Started
        </Button>
      </Box>
    </Box>
  );
};

export default ScheduleSection;
